import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';
import Button from '../components/common/Button';
import useEvents from '../hooks/useEvents';

// Sade kategori listesi
const CATEGORIES = [
  { id: 'all', label: 'Tümü' },
  { id: 'karaoke', label: 'Karaoke' },
  { id: 'parti', label: 'Parti' },
  { id: 'söyleşi', label: 'Söyleşi' },
  { id: 'konser', label: 'Konser' },
  { id: 'atölye', label: 'Atölye' },
  { id: 'workshop', label: 'Workshop' },
];

const EventsPage = () => {
  const { events, featuredEvents, loading, getEventsByCategory } = useEvents();
  const [filter, setFilter] = useState('all');
  
  // Filtreleme
  const filteredEvents = getEventsByCategory(filter);

  // Tarih formatlama
  const formatDate = (date) => {
    if (!date) return '-';
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  // Kategori label'ı getir
  const getCategoryLabel = (categoryId) => {
    const category = CATEGORIES.find(c => c.id.toLowerCase() === categoryId?.toLowerCase());
    return category?.label || categoryId;
  };
  
  return (
    <div className="bg-gray-50 min-h-screen">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative bg-indigo-600 text-white pt-24 pb-12 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 to-purple-600 opacity-90"></div>
        
        {/* Dekoratif Müzik Notaları */}
        <div className="absolute inset-0 opacity-20 overflow-hidden">
          {[...Array(10)].map((_, i) => (
            <div 
              key={i}
              className="absolute text-white animate-float"
              style={{
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                fontSize: `${Math.random() * 40 + 20}px`,
                transform: `rotate(${Math.random() * 360}deg)`,
                animationDuration: `${Math.random() * 10 + 15}s`,
                animationDelay: `${Math.random() * 5}s`
              }}
            >
              {['♪', '♫', '♬', '♩'][Math.floor(Math.random() * 4)]}
            </div>
          ))}
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 bg-white">
          <div className="max-w-2xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl font-bold mb-6 text-black drop-shadow-lg">
              Etkinlikler
            </h1>
            <p className="text-xl text-gray-800 mb-8 drop-shadow-lg">
              İYTE Müzik Topluluğu'nun düzenlediği konserler, karaoke geceleri ve daha fazlası...
            </p>
          </div>
        </div>
        
        {/* Dalgalı alt kenar */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320" className="w-full h-auto">
            <path fill="#f9fafb" fillOpacity="1" d="M0,128L80,138.7C160,149,320,171,480,165.3C640,160,800,128,960,122.7C1120,117,1280,139,1360,149.3L1440,160L1440,320L1360,320C1280,320,1120,320,960,320C800,320,640,320,480,320C320,320,160,320,80,320L0,320Z"></path>
          </svg>
        </div>
      </section>
      
      {/* Kategori Filtreleme */}
      <section className="py-8">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center flex-wrap gap-2">
            {CATEGORIES.map(category => (
              <button 
                key={category.id}
                onClick={() => setFilter(category.id)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  filter === category.id 
                    ? 'bg-indigo-600 text-white shadow-md' 
                    : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>
      </section>
      
      {/* Öne Çıkan Etkinlik */}
      {featuredEvents.length > 0 && filter === 'all' && (
        <section className="py-10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-8 text-center">Öne Çıkan Etkinlikler</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredEvents.map(event => (
                <div 
                  key={event.id} 
                  className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300"
                >
                  <div className="relative h-48 overflow-hidden bg-indigo-100">
                    <img 
                      src={event.image || '/api/placeholder/800/500'} 
                      alt={event.title}
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                      onError={(e) => {
                        e.target.src = '/api/placeholder/800/500';
                      }}
                    />
                    <div className="absolute top-4 right-4 bg-indigo-600 text-white text-xs uppercase tracking-wider font-semibold py-1 px-2 rounded">
                      {getCategoryLabel(event.category)}
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-indigo-600">{formatDate(event.date)}</span>
                      <span className="text-sm text-gray-500">{event.time}</span>
                    </div>
                    
                    <h3 className="text-xl font-bold text-gray-800 mb-2">{event.title}</h3>
                    
                    <p className="text-gray-600 mb-4 line-clamp-2">{event.description}</p>
                    
                    <div className="flex items-center justify-between mt-4">
                      <div className="flex items-center">
                        <svg className="w-4 h-4 text-gray-400 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className="text-sm text-gray-500">{event.location}</span>
                      </div>
                      
                      <span className="font-medium text-gray-900">{event.price || 'Ücretsiz'}</span>
                    </div>
                    
                    <Link to={`/events/${event.id}`} className="block mt-6">
                      <Button fullWidth className="bg-indigo-600 hover:bg-indigo-700 text-white py-2">
                        Detaylar
                      </Button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
      
      {/* Tüm Etkinlikler */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-8 text-center">
            {filter === 'all' ? 'Tüm Etkinlikler' : `${CATEGORIES.find(c => c.id === filter)?.label || ''} Etkinlikleri`}
          </h2>
          
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-2 border-indigo-600 border-t-transparent"></div>
              <p className="mt-4 text-gray-500">Etkinlikler yükleniyor...</p>
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-xl font-bold text-gray-700 mb-2">
                {filter === 'all' ? 'Henüz etkinlik eklenmemiş' : 'Bu kategoride etkinlik bulunamadı'}
              </h3>
              <p className="text-gray-500 mb-4">
                {filter === 'all' 
                  ? 'Yakında yeni etkinlikler eklenecektir.' 
                  : 'Farklı bir kategori seçerek etkinlikleri görüntüleyebilirsiniz.'}
              </p>
              {filter !== 'all' && (
                <Button onClick={() => setFilter('all')} variant="outline" className="mt-2">
                  Tüm Etkinlikleri Göster
                </Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {filteredEvents.map(event => (
                <div 
                  key={event.id} 
                  className="flex flex-col md:flex-row bg-white overflow-hidden rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 h-full"
                >
                  <div className="md:w-1/3 relative bg-indigo-100">
                    <img 
                      src={event.image || '/api/placeholder/800/500'} 
                      alt={event.title}
                      className="w-full h-full object-cover md:object-cover"
                      onError={(e) => {
                        e.target.src = '/api/placeholder/800/500';
                      }}
                    />
                    <div className="absolute top-2 left-2 bg-white bg-opacity-90 text-indigo-600 text-xs uppercase tracking-wider font-semibold py-1 px-2 rounded">
                      {getCategoryLabel(event.category)}
                    </div>
                  </div>
                  
                  <div className="md:w-2/3 p-6 flex flex-col">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-indigo-600">{formatDate(event.date)}</span>
                      <span className="text-sm text-gray-500">{event.time}</span>
                    </div>
                    
                    <h3 className="text-xl font-bold text-gray-800 mb-2">{event.title}</h3>
                    
                    <p className="text-gray-600 mb-4 flex-grow">{event.description}</p>
                    
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center">
                        <svg className="w-4 h-4 text-gray-400 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className="text-sm text-gray-500">{event.location}</span>
                      </div>
                      
                      <span className="font-medium text-gray-900">{event.price || 'Ücretsiz'}</span>
                    </div>
                    
                    <div className="mt-4 flex justify-end">
                      <Link to={`/events/${event.id}`}>
                        <Button variant="outline" className="border-indigo-600 text-indigo-600 hover:bg-indigo-50">
                          Detaylar
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
      
      {/* CTA Bölümü */}
      <section className="bg-indigo-600 py-16 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Müzik Tutkunu musun?</h2>
          <p className="text-lg text-indigo-100 mb-8 max-w-2xl mx-auto">
            İYTE Müzik Topluluğu'nun bir parçası ol ve yeteneğini göster. Müzik odası rezervasyonları ve etkinlikler hakkında detaylı bilgi için:
          </p>
          <div className="flex justify-center gap-4 flex-wrap">
            <Link to="/contact">
              <Button className="bg-white text-indigo-600 hover:bg-indigo-50">
                Bize Ulaşın
              </Button>
            </Link>
            <Link to="/reserve">
              <Button className="border-2 border-white bg-transparent hover:bg-white/10">
                Oda Rezervasyonu
              </Button>
            </Link>
          </div>
        </div>
      </section>
      
      <Footer />
      
      {/* Animasyonlar için CSS */}
      <style jsx>{`
        @keyframes float {
          0% {
            transform: translate(0, 0) rotate(0deg);
            opacity: 0.1;
          }
          50% {
            transform: translate(-20px, -20px) rotate(180deg);
            opacity: 0.3;
          }
          100% {
            transform: translate(0, 0) rotate(360deg);
            opacity: 0.1;
          }
        }
        
        .animate-float {
          animation: float 20s infinite ease-in-out;
        }
        
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default EventsPage;
